import { forwardRef, useImperativeHandle, ForwardedRef, FormEvent } from 'react';
import { IBAN, BIC } from 'ibankit';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Button, TypeInput, SelectField, Option, SingleLineText, TitlePart } from '@bloomays-lib/ui.shared';
import { ValidationTextField } from '@bloomays-lib/ui.shared';
import { ExpirationUploadField } from '../molecules/ExpirationUploadField';
import { SocietyRegistrationNumberTextField } from '../molecules/SocietyRegistrationNumberTextField';
import { AddressCountryCodeEU, ISociety } from '@bloomays-lib/types.shared';
import VATTextField from '../molecules/VATTextField';
import CountrySelector, { Country } from '../molecules/CountrySelector';
import { dateFormat } from '@bloomays-lib/utils.shared';
import { getSocietyDocExpirationConstraints } from '../../helpers/societyForm';

import { useBloomerSocietyForm } from './useBloomerSocietyForm';

export type EditBloomerSocietyRef = {
  validate: (e: FormEvent<HTMLFormElement>) => Promise<boolean>;
};

export type EditBloomerSocietyProps = {
  onValidated: (data: ISociety) => Promise<ISociety | undefined>;
  society?: ISociety;
  hideSubmitButton?: boolean;
};

/**
 * Formulaire de la société fournisseur (Bloomer). Présentation legacy (atomes V1) ; toute la
 * logique métier et l'état vivent dans le hook headless `useBloomerSocietyForm` (règles
 * mono-sourcées dans `helpers/societyForm.ts`). Ce composant ne fait que câbler le hook aux champs.
 * Contrat externe inchangé : `forwardRef` exposant `validate`, props `{ onValidated, society,
 * hideSubmitButton }` — partagé par la créa, l'onboarding et le drawer du tableau de bord V2.
 */
const EditBloomerSociety = forwardRef(
  (
    { onValidated, society: bloomerSociety, hideSubmitButton }: EditBloomerSocietyProps,
    ref: ForwardedRef<EditBloomerSocietyRef>,
  ) => {
    const { t } = useTranslation(['bloomer', 'mission', 'society']);

    const isInSocietyOptions = (value: string | undefined, options: Option[]): boolean => {
      return options.some((option) => option.value === value);
    };

    const {
      formSociety,
      setFormSociety,
      KBIS,
      setKBIS,
      URSSAF,
      setURSSAF,
      RCP,
      setRCP,
      KbisExpirationDate,
      setKbisExpirationDate,
      URSSAFVCExpirationDate,
      setURSSAFVCExpirationDate,
      RCPExpirationDate,
      setRCPVCExpirationDate,
      isFirstTime,
      setOnboardingDraftDates,
      expirationDateErrors,
      setExpirationDateErrors,
      preventText,
      setPreventText,
      uploading,
      UploadKBISRef,
      UploadURSSAFRef,
      UploadRCPRef,
      isMicroEntrepreneur,
      isEuropeanSociety,
      isForeignSociety,
      requiredVATNumber,
      showVATTextField,
      fieldMode,
      kbisRequired,
      urssafRequired,
      societyKBISLabel,
      societyTaxesLabel,
      societyInsuranceLabel,
      mustHaveKbisExpiration,
      mustHaveUrssafExpiration,
      mustHaveRcpExpiration,
      supportIban,
      selectSocietyOption,
      resetFormSociety,
      validate,
    } = useBloomerSocietyForm({ society: bloomerSociety, onValidated });

    useImperativeHandle(ref, () => ({
      validate(e: any) {
        return validate(e);
      },
    }));

    return (
      <Container>
        <TitlePart textTitle="À propos de qui va nous envoyer" span="ta facture" />
        <ValidationTextField
          aria-label="bloomersocietyinfo-name"
          id="societyName"
          name="societyName"
          label="Nom"
          value={formSociety?.name || ''}
          type={TypeInput.text}
          required={true}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFormSociety({
              ...formSociety,
              name: e.target.value,
            } as ISociety);
          }}
          formElement={true}
        />
        <SelectField
          data-testid="portage"
          aria-label="bloomersocietyinfo-portage"
          id="societyPortage"
          name="societyPortage"
          label="C'est une société de portage ?"
          defaultOption={true}
          value={formSociety?.portage === undefined ? '' : formSociety?.portage ? '1' : '0'}
          required={true}
          options={[
            { value: '0', label: 'Non' },
            { value: '1', label: 'Oui' },
          ]}
          handlechange={(e) => {
            e.preventDefault();
            const isPortage = e.target.value === '1';
            if (isMicroEntrepreneur && isPortage) {
              setFormSociety({
                ...formSociety,
                status: undefined,
                portage: isPortage,
              } as ISociety);
            } else {
              setFormSociety({
                ...formSociety,
                portage: isPortage,
              } as ISociety);
            }
          }}
        />
        <SelectField
          data-testid="societyStatus"
          aria-label="bloomersocietyinfo-status"
          id="societyStatus"
          name="societyStatus"
          label="Status (forme juridique)"
          defaultOption={true}
          value={isInSocietyOptions(formSociety?.status, selectSocietyOption) ? formSociety?.status : ''}
          required={true}
          options={selectSocietyOption}
          handlechange={(e) => {
            e.preventDefault();
            setFormSociety({
              ...formSociety,
              status: e.target.value,
            } as ISociety);
          }}
        />
        <CountrySelector
          id="societyCountry"
          name="societyCountry"
          value={formSociety?.addressCountryCode || undefined}
          required={true}
          onChange={(e?: Country) => {
            setFormSociety({
              ...formSociety,
              addressCountryCode: e?.iso2,
            } as ISociety);
          }}
          allowedCountryCode={(code: string) => {
            if (isForeignSociety) {
              return code !== 'FR' && !AddressCountryCodeEU.includes(code);
            } else {
              if (isEuropeanSociety) {
                return code !== 'FR' && AddressCountryCodeEU.includes(code);
              }
              return code === 'FR';
            }
          }}
          hidden={!(isForeignSociety || isEuropeanSociety)}
        />
        <SocietyRegistrationNumberTextField
          fieldMode={fieldMode}
          aria-label="bloomersocietyinfo-siret"
          id="societySiret"
          name={formSociety?.siret}
          required={true}
          value={formSociety?.siret || ''}
          onChange={(e) => {
            if (e.target.value !== bloomerSociety?.siret && bloomerSociety?.siret) {
              resetFormSociety(e.target.value);
            } else {
              setFormSociety({
                ...formSociety,
                KBIS: undefined,
                siret: e.target.value,
              } as ISociety);
              setPreventText(false);
            }
          }}
          formElement={true}
        />
        {preventText && (
          <SingleLineText
            text="Attention en changeant le siret, tu t'apprêtes à créer une nouvelle société !"
            sx={{ paddingBottom: '1em', color: 'red' }}
          />
        )}
        <VATTextField
          aria-label="bloomersocietyinfo-vat"
          id="societyTVA"
          name={formSociety?.tva}
          hidden={!showVATTextField}
          required={requiredVATNumber}
          value={formSociety?.tva || ''}
          onChange={(e) => {
            setFormSociety({
              ...formSociety,
              KBIS: undefined,
              tva: e.target.value,
            } as ISociety);
          }}
          formElement={true}
        />
        {isMicroEntrepreneur && (
          <SingleLineText
            text="Le numéro de TVA est obligatoire si assujeti à la TVA pour traiter vos factures."
            sx={{ paddingBottom: '1em' }}
          />
        )}
        <ValidationTextField
          aria-label="bloomersocietyinfo-iban"
          id="societyIban"
          name={formSociety?.IBAN}
          label={
            supportIban
              ? t('ibanLabel', { ns: 'mission' })
              : t('ibanFallbackLabel', {
                  ns: 'mission',
                })
          }
          value={formSociety?.IBAN || ''}
          type={TypeInput.text}
          placeholder={supportIban ? 'FR12 1234 1234 1234 1234 1234 12' : 'XXXXXXXX'}
          hidden={formSociety?.portage}
          required={!formSociety?.portage}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (supportIban && !IBAN.isValid((e.target as HTMLInputElement).value)) {
              (e.target as HTMLInputElement).setCustomValidity('IBAN mal formé');
            } else {
              (e.target as HTMLInputElement).setCustomValidity('');
            }
            setFormSociety({
              ...formSociety,
              IBAN: (e.target as HTMLInputElement).value,
            } as ISociety);
          }}
          formElement={true}
        />
        <ValidationTextField
          aria-label="bloomersocietyinfo-bic"
          id="societyBic"
          name={formSociety?.BIC}
          label={
            supportIban
              ? t('bicLabel', { ns: 'mission' })
              : t('bicFallbackLabel', {
                  ns: 'mission',
                })
          }
          value={formSociety?.BIC || ''}
          type={TypeInput.text}
          minLength={'8'}
          maxLength={'11'}
          hidden={formSociety?.portage}
          placeholder="8 à 11 caractères"
          required={!formSociety?.portage}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!BIC.isValid(e.target.value)) {
              e.target.setCustomValidity(supportIban ? 'BIC' : 'SWIFT / BIC');
            } else {
              e.target.setCustomValidity('');
            }
            setFormSociety({
              ...formSociety,
              BIC: e.target.value,
            } as ISociety);
          }}
          formElement={true}
        />
        <ExpirationUploadField
          title={societyKBISLabel}
          uploadAriaLabel="bloomersocietyinfo-kbis"
          uploadId="KBIS"
          uploadName="KBIS"
          uploadPlaceholder={societyKBISLabel}
          uploadTextLabel="Ajouter"
          uploadRequired={kbisRequired}
          preloadedFiles={KBIS ? [KBIS] : []}
          onUploadChange={(files) => {
            setKBIS(files.length ? files[0] : null);
            setKbisExpirationDate(undefined);
          }}
          fileTypes={['.pdf']}
          maxFiles={1}
          maxLength={5000000}
          uploadAutoload={true}
          uploadDisplayIcons={true}
          uploadRef={UploadKBISRef}
          expirationLabel={mustHaveKbisExpiration ? `${t('expirationDate')} *` : t('expirationDate')}
          expirationValue={KbisExpirationDate ? new Date(KbisExpirationDate) : undefined}
          onExpirationChange={(iso: string) => {
            const value = dateFormat(new Date(iso), 'yyyy-MM-dd');
            setKbisExpirationDate(value);
            setExpirationDateErrors((prev) => ({ ...prev, kbis: undefined }));
            if (isFirstTime) {
              setOnboardingDraftDates((prev) => ({ ...prev, KbisExpirationDate: value }));
            }
          }}
          expirationError={expirationDateErrors.kbis}
          onExpirationErrorClear={() => setExpirationDateErrors((prev) => ({ ...prev, kbis: undefined }))}
          {...getSocietyDocExpirationConstraints('kbis')}
        />
        {preventText && formSociety?.recordId && (
          <SingleLineText
            text="Tu t'apprêtes à créer une nouvelle société, tu dois donc uploader un nouveau KBIS / attestation sirene / INSEE"
            sx={{ paddingBottom: '1em' }}
          />
        )}
        <ExpirationUploadField
          title={societyTaxesLabel}
          uploadAriaLabel="bloomersocietyinfo-urssaf"
          uploadId="URSSAFVigilanceCertificate"
          uploadName="URSSAFVigilanceCertificate"
          uploadPlaceholder={societyTaxesLabel}
          uploadTextLabel="Ajouter"
          uploadRequired={urssafRequired}
          preloadedFiles={URSSAF ? [URSSAF] : []}
          onUploadChange={(files) => {
            setURSSAF(files.length ? files[0] : null);
            setURSSAFVCExpirationDate(undefined);
          }}
          fileTypes={['.pdf']}
          maxFiles={1}
          maxLength={5000000}
          uploadAutoload={true}
          uploadDisplayIcons={true}
          uploadRef={UploadURSSAFRef}
          expirationLabel={mustHaveUrssafExpiration ? `${t('expirationDate')} *` : t('expirationDate')}
          expirationValue={URSSAFVCExpirationDate ? new Date(URSSAFVCExpirationDate) : undefined}
          onExpirationChange={(iso: string) => {
            const value = dateFormat(new Date(iso), 'yyyy-MM-dd');
            setURSSAFVCExpirationDate(value);
            setExpirationDateErrors((prev) => ({ ...prev, urssaf: undefined }));
            if (isFirstTime) {
              setOnboardingDraftDates((prev) => ({ ...prev, URSSAFVCExpirationDate: value }));
            }
          }}
          expirationError={expirationDateErrors.urssaf}
          onExpirationErrorClear={() => setExpirationDateErrors((prev) => ({ ...prev, urssaf: undefined }))}
          {...getSocietyDocExpirationConstraints('urssaf')}
        />
        <ExpirationUploadField
          title={societyInsuranceLabel}
          uploadAriaLabel="bloomersocietyinfo-rcp"
          uploadId="RCPVigilanceCertificate"
          uploadName="RCPVigilanceCertificate"
          uploadPlaceholder={societyInsuranceLabel}
          uploadTextLabel="Ajouter"
          uploadRequired={false}
          preloadedFiles={RCP ? [RCP] : []}
          onUploadChange={(files) => setRCP(files.length ? files[0] : null)}
          fileTypes={['.pdf']}
          maxFiles={1}
          maxLength={5000000}
          uploadAutoload={true}
          uploadDisplayIcons={true}
          uploadRef={UploadRCPRef}
          expirationLabel={mustHaveRcpExpiration ? `${t('expirationDate')} *` : t('expirationDate')}
          expirationValue={RCPExpirationDate ? new Date(RCPExpirationDate) : undefined}
          onExpirationChange={(iso: string) => {
            const value = dateFormat(new Date(iso), 'yyyy-MM-dd');
            setRCPVCExpirationDate(value);
            setExpirationDateErrors((prev) => ({ ...prev, rcp: undefined }));
            if (isFirstTime) {
              setOnboardingDraftDates((prev) => ({ ...prev, RCPExpirationDate: value }));
            }
          }}
          expirationError={expirationDateErrors.rcp}
          onExpirationErrorClear={() => setExpirationDateErrors((prev) => ({ ...prev, rcp: undefined }))}
          {...getSocietyDocExpirationConstraints('rcp')}
          infoContent={
            "RCP, c'est quoi ? 👉 Votre entreprise peut causer dans le cadre de son activité un préjudice à un tiers. Ce dernier relève de la Responsabilité Civile Professionnelle  de votre entreprise. L’assurance RC Pro couvre alors votre entreprise en remboursant à la victime concernée tout ou partie des dommages occasionnés."
          }
        />
        {!hideSubmitButton && (
          <Button
            buttonType="submit"
            // eslint-disable-next-line jsx-a11y/aria-role
            role={'button-editbloomersociety'}
            none={'none'}
            textButton={uploading ? "En cours d'envoi" : 'Sauvegarder mes données'}
            disable={uploading}
          />
        )}
      </Container>
    );
  },
);

export default EditBloomerSociety;

export { EditBloomerSociety };
EditBloomerSociety.displayName = 'EditBloomerSociety';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  backgroundColor: theme.palette.neutral.white,
  boxShadow: '2px 2px 20px lightgrey',
  borderRadius: '10px',
  width: '100%',
  margin: '1em auto',
  padding: '1.5em',
}));
